import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ExportIcon, PlusIcon, UploadIcon } from '@/components/icons';

export enum CustomButtonType {
  NO_ICON = 'NO_ICON',
  EXPORT_ICON = 'EXPORT_ICON',
  ADD_ICON = 'ADD_ICON',
  WHITE_BG = 'WHITE_BG',
  UPLOAD = 'UPLOAD',
  ADD_STATE = 'ADD_STATE',
}

export interface CustomButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonType: CustomButtonType;
  title: string;
}

const PageButton = styled.button<{ $buttonType: CustomButtonType }>`
  display: flex;
  height: 40px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  gap: 8px;
  cursor: pointer;
  background: ${props =>
    props.$buttonType === CustomButtonType.WHITE_BG
      ? '#fff'
      : props.$buttonType === CustomButtonType.UPLOAD ||
        props.$buttonType === CustomButtonType.ADD_STATE
      ? '#E34A66'
      : '#3080EC'};
  border: ${props =>
    props.$buttonType === CustomButtonType.WHITE_BG
      ? '1px solid #DEE1EB'
      : 'none'};
  white-space: nowrap;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
  span {
    color: ${props =>
      props.$buttonType === CustomButtonType.WHITE_BG ? '#202634' : '#fff'};
  }
  svg * {
    opacity: 1;
  }
  &:disabled {
    cursor: not-allowed;
    background: ${props =>
      props.$buttonType === CustomButtonType.WHITE_BG ? '#f2f4f7' : '#b8c1d4'};
    border: ${props =>
      props.$buttonType === CustomButtonType.WHITE_BG
        ? '1px solid #e0e4ed'
        : 'none'};
    span {
      color: ${props =>
        props.$buttonType === CustomButtonType.WHITE_BG
          ? '#9aa3b5'
          : '#ffffff'};
    }
    svg * {
      opacity: 0.6;
    }
  }
`;

const Text = styled.span`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

const CustomButton: React.FC<CustomButtonProps> = ({
  buttonType,
  title = '',
  ...rest
}: CustomButtonProps) => {
  const renderButton = useMemo(() => {
    switch (buttonType) {
      case CustomButtonType.EXPORT_ICON:
        return (
          <>
            <ExportIcon />
            <Text>{title}</Text>
          </>
        );
      case CustomButtonType.ADD_STATE:
      case CustomButtonType.ADD_ICON:
        return (
          <>
            <PlusIcon />
            <Text>{title}</Text>
          </>
        );

      case CustomButtonType.UPLOAD:
        return (
          <>
            <UploadIcon />
            <Text>{title}</Text>
          </>
        );

      case CustomButtonType.NO_ICON:
        return <Text>{title}</Text>;

      default:
        return <Text>{title}</Text>;
    }
  }, [buttonType, title]);

  return (
    <PageButton type={`button`} $buttonType={buttonType} {...rest}>
      {renderButton}
    </PageButton>
  );
};

export default CustomButton;
